import axiosClient from "../../config/axiosClient";
import { ENDPOINTS } from "../../config/endpoints";
import {
  ChatMessage,
  CreateConversationPayload,
  PaginatedConversations,
  PaginatedMessages,
  UploadAttachmentResponse,
  SignedUrlResponse,
  AdminPaginatedConversations,
} from "./chat.types";

const unwrap = <T>(payload: any): T => {
  // Supports both shapes:
  // 1) { success: true, data: T }
  // 2) { success: true, data: { success: true, data: T } }  (when backend also wraps + TransformInterceptor wraps)
  if (payload && typeof payload === "object" && "success" in payload && "data" in payload) {
    const level1 = payload.data;
    if (level1 && typeof level1 === "object" && "success" in level1 && "data" in level1) {
      return (level1 as any).data as T;
    }
    return level1 as T;
  }
  return payload as T;
};

class ChatService {
  async createOrGetConversation(payload: CreateConversationPayload) {
    const response = await axiosClient.post<{ success: boolean; data: any }>(
      ENDPOINTS.CHAT.CONVERSATIONS,
      payload
    );
    return unwrap<any>(response.data);
  }

  async sendMessageHttp(
    conversationId: string,
    body: { text?: string; attachments?: { key: string }[] }
  ): Promise<ChatMessage> {
    const response = await axiosClient.post<{ success: boolean; data: ChatMessage }>(
      ENDPOINTS.CHAT.conversationMessages(conversationId),
      body
    );
    return unwrap<ChatMessage>(response.data);
  }

  async listConversations(params?: { page?: number; limit?: number }): Promise<PaginatedConversations> {
    const response = await axiosClient.get<{ success: boolean; data: PaginatedConversations }>(
      ENDPOINTS.CHAT.CONVERSATIONS,
      { params }
    );
    return unwrap<PaginatedConversations>(response.data);
  }

  async listMessages(
    conversationId: string,
    params?: { cursor?: string; limit?: number }
  ): Promise<PaginatedMessages> {
    const response = await axiosClient.get<{ success: boolean; data: PaginatedMessages }>(
      ENDPOINTS.CHAT.conversationMessages(conversationId),
      { params }
    );
    return unwrap<PaginatedMessages>(response.data);
  }

  async markRead(conversationId: string): Promise<void> {
    await axiosClient.post(ENDPOINTS.CHAT.markRead(conversationId));
  }

  async uploadAttachment(file: File): Promise<UploadAttachmentResponse> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await axiosClient.post<{ success: boolean; data: UploadAttachmentResponse }>(
      ENDPOINTS.CHAT.ATTACHMENTS,
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return unwrap<UploadAttachmentResponse>(response.data);
  }

  async getAttachmentUrl(key: string): Promise<SignedUrlResponse> {
    const response = await axiosClient.get<{ success: boolean; data: SignedUrlResponse }>(
      ENDPOINTS.CHAT.attachmentUrl(key)
    );
    return unwrap<SignedUrlResponse>(response.data);
  }

  // Admin-only methods (use ADMIN_CHAT endpoints)
  async listAdminConversations(params?: {
    page?: number;
    limit?: number;
    buyerId?: string;
    sellerId?: string;
    fromDate?: string;
    toDate?: string;
  }): Promise<AdminPaginatedConversations> {
    const response = await axiosClient.get<{ success: boolean; data: AdminPaginatedConversations }>(
      ENDPOINTS.ADMIN_CHAT.CONVERSATIONS,
      { params }
    );
    return unwrap<AdminPaginatedConversations>(response.data);
  }

  async listAdminMessages(
    conversationId: string,
    params?: { cursor?: string; limit?: number }
  ): Promise<PaginatedMessages> {
    const response = await axiosClient.get<{ success: boolean; data: PaginatedMessages }>(
      ENDPOINTS.ADMIN_CHAT.conversationMessages(conversationId),
      { params }
    );
    return unwrap<PaginatedMessages>(response.data);
  }

  async getAdminAttachmentUrl(key: string): Promise<SignedUrlResponse> {
    const response = await axiosClient.get<{ success: boolean; data: SignedUrlResponse }>(
      ENDPOINTS.ADMIN_CHAT.attachmentUrl(key)
    );
    return unwrap<SignedUrlResponse>(response.data);
  }
}

export const chatService = new ChatService();

